import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Trash2, 
  RefreshCw, 
  Server, 
  Wrench, 
  Shield, 
  Activity,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Settings2,
  Play,
  FileText,
  Clock,
  ChevronRight,
  ChevronLeft,
  Zap,
  Globe,
  Key,
  Filter,
  Search,
  Pencil,
  Info,
  Bot,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Power,
  PowerOff,
  FolderOpen
} from 'lucide-react';
import { format } from 'date-fns';
import { apiClient as api } from '@/lib/api';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { SectionNav, type SectionNavSectionDef } from '@easyflow/ui-frontend/components/ui/section-nav';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@easyflow/ui-frontend/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage, getApiErrorMessage } from '../../lib/utils';
// Task #3819 — DTO risposta condivisi (Zod + z.infer nel pacchetto shared).
import type { McpCustomSyncResponse } from '@easyflow/shared';


interface MCPServer {
  id: string;
  name: string;
  description: string | null;
  transport: 'http-sse' | 'streamable-http' | 'internal';
  endpoint: string | null;
  auth_type: string | null;
  timeout_ms: number;
  retry_count: number;
  enabled: boolean;
  health_status: 'connected' | 'disconnected' | 'error' | 'unknown';
  last_health_check: string | null;
  tool_count: number;
  created_at: string;
  oauth2_token_url?: string | null;
  oauth2_scope?: string | null;
}

interface MCPTool {
  id: string;
  tool_name: string;
  description: string | null;
  input_schema: Record<string, unknown>;
  annotations: Record<string, unknown>;
  category: string | null;
  cached_at: string;
  allowed: boolean;
  require_confirmation: boolean;
  max_calls_per_session: number | null;
  enabled: boolean;
}

interface MCPAuditLog {
  id: string;
  server_name: string;
  tool_name: string;
  status: 'success' | 'error';
  duration_ms: number;
  input_params: Record<string, unknown>;
  output_result: unknown;
  error_message: string | null;
  created_at: string;
}

type TransportType = 'internal' | 'http-sse' | 'streamable-http';

interface ServerFormData {
  name: string;
  description: string;
  transport: TransportType;
  endpoint: string;
  auth_type: string;
  auth_credentials: string;
  oauth2_client_id: string;
  oauth2_client_secret: string;
  oauth2_token_url: string;
  oauth2_scope: string;
  timeout_ms: number;
  retry_count: number;
}

const MCP_TOP_SECTIONS: SectionNavSectionDef[] = [
  { value: 'catalog', label: 'Catalogo' },
  { value: 'custom', label: 'Custom / Self-hosted' },
];

const WIZARD_STEPS = [
  { id: 1, title: 'Transport', description: 'Scegli il tipo di connessione' },
  { id: 2, title: 'Connessione', description: 'Configura endpoint o comando' },
  { id: 3, title: 'Autenticazione', description: 'Imposta credenziali' },
  { id: 4, title: 'Impostazioni', description: 'Timeout e retry' },
  { id: 5, title: 'Test', description: 'Verifica connessione' },
];

function AddServerWizard({ open, onOpenChange, onSuccess, editServer }: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  editServer?: MCPServer | null;
}) {
  const { toast } = useToast();
  const isEditMode = !!editServer;
  const [currentStep, setCurrentStep] = useState(1);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState<string | null>(null);
  // Task #3819 — esito discovery post-creazione (sync automatico).
  const [postCreateSync, setPostCreateSync] = useState<'idle' | 'syncing' | 'done' | 'error'>('idle');
  const [discovery, setDiscovery] = useState<McpCustomSyncResponse | null>(null);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);

  const emptyForm: ServerFormData = {
    name: '',
    description: '',
    transport: 'streamable-http',
    endpoint: '',
    auth_type: 'none',
    auth_credentials: '',
    oauth2_client_id: '',
    oauth2_client_secret: '',
    oauth2_token_url: '',
    oauth2_scope: '',
    timeout_ms: 30000,
    retry_count: 3
  };

  const [formData, setFormData] = useState<ServerFormData>(emptyForm);

  useEffect(() => {
    if (editServer && open) {
      setFormData({
        name: editServer.name || '',
        description: editServer.description || '',
        transport: editServer.transport || 'streamable-http',
        endpoint: editServer.endpoint || '',
        auth_type: editServer.auth_type || 'none',
        auth_credentials: '',
        oauth2_client_id: '',
        oauth2_client_secret: '',
        oauth2_token_url: editServer.oauth2_token_url || '',
        oauth2_scope: editServer.oauth2_scope || '',
        timeout_ms: editServer.timeout_ms || 30000,
        retry_count: editServer.retry_count || 3
      });
      setCurrentStep(1);
      setTestStatus('idle');
      setTestError(null);
    }
  }, [editServer, open]);

  const resetWizard = () => {
    setCurrentStep(1);
    setTestStatus('idle');
    setTestError(null);
    setFormData(emptyForm);
    setPostCreateSync('idle');
    setDiscovery(null);
    setDiscoveryError(null);
  };

  const buildAuthCredentials = (data: typeof formData): string | undefined => {
    if (data.auth_type === 'oauth2') {
      if (!data.oauth2_client_id && !data.oauth2_client_secret && isEditMode) return undefined;
      const obj: Record<string, string> = {};
      if (data.oauth2_client_id) obj.client_id = data.oauth2_client_id;
      if (data.oauth2_client_secret) obj.client_secret = data.oauth2_client_secret;
      if (data.oauth2_token_url) obj.token_url = data.oauth2_token_url;
      if (data.oauth2_scope) obj.scope = data.oauth2_scope;
      return Object.keys(obj).length > 0 ? JSON.stringify(obj) : undefined;
    }
    return data.auth_credentials || undefined;
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        auth_credentials: buildAuthCredentials(data),
        oauth2_client_id: undefined,
        oauth2_client_secret: undefined,
        oauth2_token_url: undefined,
        oauth2_scope: undefined,
      };
      const res = await api.post<{ id: string }>('/mcp/servers', payload);
      return res.data;
    },
    onSuccess: async (created) => {
      toast({ title: 'Server MCP aggiunto con successo' });
      onSuccess();
      // Task #3819 — discovery automatica post-creazione: sync + esito nel wizard.
      if (!created?.id) {
        onOpenChange(false);
        resetWizard();
        return;
      }
      setPostCreateSync('syncing');
      try {
        const res = await api.post<McpCustomSyncResponse>(`/mcp/servers/${created.id}/sync`);
        setDiscovery(res.data ?? null);
        setPostCreateSync('done');
        onSuccess();
      } catch (error) {
        setDiscoveryError(getApiErrorMessage(error) || getErrorMessage(error) || 'Discovery fallita');
        setPostCreateSync('error');
      }
    },
    onError: (error: unknown) => {
      toast({ 
        title: 'Errore', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload: Record<string, unknown> = {
        name: data.name,
        description: data.description,
        transport: data.transport,
        endpoint: data.endpoint || undefined,
        auth_type: data.auth_type,
        timeout_ms: data.timeout_ms,
        retry_count: data.retry_count,
      };
      const creds = buildAuthCredentials(data);
      if (creds) {
        payload.auth_credentials = creds;
      }
      const res = await api.patch(`/mcp/servers/${editServer!.id!}`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast({ title: 'Server MCP aggiornato con successo' });
      onOpenChange(false);
      resetWizard();
      onSuccess();
    },
    onError: (error: unknown) => {
      toast({ 
        title: 'Errore', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    }
  });

  const testConnection = async () => {
    setTestStatus('testing');
    setTestError(null);
    try {
      const payload = {
        transport: formData.transport,
        endpoint: formData.endpoint,
        auth_type: formData.auth_type,
        auth_credentials: buildAuthCredentials(formData),
        timeout_ms: formData.timeout_ms
      };
      await api.post('/mcp/test-connection', payload);
      setTestStatus('success');
    } catch (error) {
      setTestStatus('error');
      setTestError(getApiErrorMessage(error) || getErrorMessage(error) || 'Connessione fallita');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.transport;
      case 2:
        return !!formData.endpoint;
      case 3:
        if (formData.auth_type === 'oauth2' && !isEditMode) {
          return !!formData.oauth2_client_id && !!formData.oauth2_client_secret && !!formData.oauth2_token_url;
        }
        if (formData.auth_type !== 'none' && formData.auth_type !== 'oauth2' && !isEditMode) return !!formData.auth_credentials;
        return true;
      case 4:
        return formData.timeout_ms > 0 && formData.retry_count >= 0;
      case 5:
        return testStatus === 'success';
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleFinish = () => {
    if (isEditMode) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetWizard(); }}>
      <DialogContent 
        className="sm:max-w-[600px]"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Modifica Server MCP' : 'Aggiungi Server MCP'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Modifica la configurazione del server MCP' : 'Configura la connessione al server MCP esterno'}
          </DialogDescription>
        </DialogHeader>

        {postCreateSync !== 'idle' ? (
          /* Task #3819 — pannello discovery post-creazione (sync automatico). */
          <>
            <div className="min-h-[250px] py-4">
              {postCreateSync === 'syncing' && (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                  <p className="font-medium">Discovery dei tool in corso...</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Connessione al server MCP e sincronizzazione dei tool disponibili
                  </p>
                </div>
              )}
              {postCreateSync === 'done' && discovery && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <p className="font-medium">
                      {discovery.tool_count} tool scoperti e salvati
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    I tool sono subito disponibili; puoi gestire permessi e conferme dalla scheda del server.
                  </p>
                  <ScrollArea className="h-[220px] border rounded-md">
                    <div className="p-2 space-y-1">
                      {discovery.tools.map((tool) => (
                        <div key={tool.tool_name} className="flex items-start justify-between gap-2 p-2 rounded hover:bg-muted/50">
                          <div className="min-w-0">
                            <div className="font-mono text-sm truncate">{tool.tool_name}</div>
                            {tool.description && (
                              <div className="text-xs text-muted-foreground line-clamp-2">{tool.description}</div>
                            )}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Badge variant={tool.allowed ? 'outline' : 'destructive'}>
                              {tool.allowed ? 'Consentito' : 'Bloccato'}
                            </Badge>
                            {tool.require_confirmation && (
                              <Badge variant="secondary">Conferma</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
              {postCreateSync === 'error' && (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                  <p className="font-medium">Server salvato, discovery non riuscita</p>
                  <p className="text-sm text-muted-foreground mt-2">{discoveryError}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Puoi riprovare la sincronizzazione dalla scheda del server.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                onClick={() => { onOpenChange(false); resetWizard(); }}
                disabled={postCreateSync === 'syncing'}
              >
                Chiudi
              </Button>
            </DialogFooter>
          </>
        ) : (
        <>
        <div className="flex items-center justify-between mb-4">
          {WIZARD_STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className={`flex flex-col items-center ${currentStep >= step.id ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep > step.id ? 'bg-primary text-primary-foreground' :
                  currentStep === step.id ? 'border-2 border-primary' : 'border border-muted-foreground'
                }`}>
                  {currentStep > step.id ? <CheckCircle2 className="h-4 w-4" /> : step.id}
                </div>
                <span className="text-xs mt-1 hidden sm:block">{step.title}</span>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="min-h-[250px] py-4">
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Nome Server *</Label>
                <Input
                  id="name"
                  placeholder="es. CRM Integration"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  placeholder="Descrizione opzionale del server"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Tipo Transport *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'streamable-http', label: 'HTTP Streamable', icon: Globe, desc: 'Raccomandato' },
                    { value: 'http-sse', label: 'HTTP SSE', icon: Activity, desc: 'Legacy' },
                  ].map((t) => (
                    <div
                      key={t.value}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.transport === t.value ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setFormData({ ...formData, transport: t.value as TransportType })}
                    >
                      <t.icon className="h-5 w-5 mb-1" />
                      <div className="font-medium text-sm">{t.label}</div>
                      <div className="text-xs text-muted-foreground">{t.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="endpoint">Endpoint URL *</Label>
                <Input
                  id="endpoint"
                  placeholder="https://mcp-server.example.com/sse"
                  value={formData.endpoint}
                  onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.transport === 'http-sse' 
                    ? 'URL completo del server SSE' 
                    : 'URL del server MCP Streamable HTTP'}
                </p>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label>Tipo Autenticazione</Label>
                <Select 
                  value={formData.auth_type} 
                  onValueChange={(v) => setFormData({ ...formData, auth_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna</SelectItem>
                    <SelectItem value="bearer">Bearer Token</SelectItem>
                    <SelectItem value="api-key">API Key (X-API-Key)</SelectItem>
                    <SelectItem value="mcp-key">MCP Key (X-MCP-Key)</SelectItem>
                    <SelectItem value="basic">Basic Auth</SelectItem>
                    <SelectItem value="oauth2">OAuth2 (Client Credentials)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.auth_type === 'oauth2' && (
                <div className="space-y-3">
                  <div className="grid gap-2">
                    <Label htmlFor="oauth2_token_url">Token URL</Label>
                    <Input
                      id="oauth2_token_url"
                      type="url"
                      placeholder="https://auth.example.com/oauth/token"
                      value={formData.oauth2_token_url}
                      onChange={(e) => setFormData({ ...formData, oauth2_token_url: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="oauth2_client_id">Client ID</Label>
                    <Input
                      id="oauth2_client_id"
                      type="text"
                      placeholder="client_id..."
                      value={formData.oauth2_client_id}
                      onChange={(e) => setFormData({ ...formData, oauth2_client_id: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="oauth2_client_secret">Client Secret</Label>
                    <Input
                      id="oauth2_client_secret"
                      type="password"
                      placeholder={isEditMode ? '(invariato — inserisci per sostituire)' : 'client_secret...'}
                      value={formData.oauth2_client_secret}
                      onChange={(e) => setFormData({ ...formData, oauth2_client_secret: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="oauth2_scope">Scope (opzionale)</Label>
                    <Input
                      id="oauth2_scope"
                      type="text"
                      placeholder="api read write..."
                      value={formData.oauth2_scope}
                      onChange={(e) => setFormData({ ...formData, oauth2_scope: e.target.value })}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    {isEditMode 
                      ? 'Lascia Client ID e Secret vuoti per mantenere le credenziali attuali.' 
                      : 'Le credenziali vengono criptate prima di essere salvate'}
                  </p>
                </div>
              )}
              {formData.auth_type !== 'none' && formData.auth_type !== 'oauth2' && (
                <div className="grid gap-2">
                  <Label htmlFor="auth_credentials">
                    {formData.auth_type === 'bearer' ? 'Token' :
                     formData.auth_type === 'api-key' ? 'API Key' :
                     formData.auth_type === 'mcp-key' ? 'MCP Key' :
                     'Username:Password'}
                  </Label>
                  <Input
                    id="auth_credentials"
                    type="password"
                    placeholder={
                      formData.auth_type === 'bearer' ? 'Bearer token...' :
                      formData.auth_type === 'api-key' ? 'sk-...' :
                      formData.auth_type === 'mcp-key' ? 'sk_live_...' :
                      'user:password'
                    }
                    value={formData.auth_credentials}
                    onChange={(e) => setFormData({ ...formData, auth_credentials: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    {isEditMode 
                      ? 'Lascia vuoto per mantenere le credenziali attuali. Inserisci un nuovo valore per sostituirle.' 
                      : 'Le credenziali vengono criptate prima di essere salvate'}
                  </p>
                </div>
              )}
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="timeout_ms">Timeout (ms)</Label>
                <Input
                  id="timeout_ms"
                  type="number"
                  value={formData.timeout_ms}
                  onChange={(e) => setFormData({ ...formData, timeout_ms: parseInt(e.target.value) || 30000 })}
                />
                <p className="text-xs text-muted-foreground">
                  Tempo massimo di attesa per ogni richiesta
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="retry_count">Numero Retry</Label>
                <Input
                  id="retry_count"
                  type="number"
                  min={0}
                  max={10}
                  value={formData.retry_count}
                  onChange={(e) => setFormData({ ...formData, retry_count: parseInt(e.target.value) || 3 })}
                />
                <p className="text-xs text-muted-foreground">
                  Tentativi di riconnessione in caso di errore
                </p>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-4">
              <div className="text-center py-4">
                {testStatus === 'idle' && (
                  <>
                    <Server className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="mb-4">Pronto per testare la connessione</p>
                    <Button onClick={testConnection}>
                      <Play className="mr-2 h-4 w-4" /> Test Connessione
                    </Button>
                  </>
                )}
                {testStatus === 'testing' && (
                  <>
                    <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
                    <p>Connessione in corso...</p>
                  </>
                )}
                {testStatus === 'success' && (
                  <>
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <p className="text-green-600 font-medium">Connessione riuscita!</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Il server MCP è raggiungibile e pronto
                    </p>
                  </>
                )}
                {testStatus === 'error' && (
                  <>
                    <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
                    <p className="text-red-600 font-medium">Connessione fallita</p>
                    <p className="text-sm text-muted-foreground mt-2">{testError}</p>
                    <Button variant="outline" className="mt-4" onClick={testConnection}>
                      <RefreshCw className="mr-2 h-4 w-4" /> Riprova
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={prevStep} disabled={currentStep === 1}>
            <ChevronLeft className="mr-2 h-4 w-4" /> Indietro
          </Button>
          {currentStep < 5 ? (
            <Button onClick={nextStep} disabled={!canProceed()}>
              Avanti <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleFinish} disabled={testStatus !== 'success' || isSaving}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isEditMode ? 'Aggiorna Server' : 'Salva Server'}
            </Button>
          )}
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config = {
    connected: { color: 'bg-green-500', text: 'Connesso', icon: CheckCircle2 },
    disconnected: { color: 'bg-gray-400', text: 'Disconnesso', icon: XCircle },
    error: { color: 'bg-red-500', text: 'Errore', icon: AlertCircle },
    unknown: { color: 'bg-yellow-500', text: 'Sconosciuto', icon: AlertCircle },
  }[status] || { color: 'bg-gray-400', text: status, icon: AlertCircle };

  return (
    <Badge variant="outline" className="gap-1">
      <span className={`w-2 h-2 rounded-full ${config.color}`} />
      {config.text}
    </Badge>
  );
}

function OverviewTab({ server }: { server: MCPServer }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const healthCheckMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ data: { status: string } }>(`/mcp/servers/${server.id}/health-check`);
      return res;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
      // Envelope permissivo: lo status può essere in data.data o direttamente sull'envelope.
      const envelope = data as { data?: { status?: string }; status?: string };
      const status = envelope?.data?.status || envelope?.status;
      toast({ 
        title: status === 'connected' ? 'Server raggiungibile' : 'Health check completato',
        description: `Status: ${status || 'unknown'}`
      });
    },
    onError: (error: unknown) => {
      toast({ 
        title: 'Health check fallito', 
        description: getErrorMessage(error),
        variant: 'destructive' 
      });
    }
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Transport</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span className="font-medium">{server.transport}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusBadge status={server.health_status} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Tools Disponibili</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              <span className="text-2xl font-bold">{server.tool_count}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Ultimo Check</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className="text-sm">
                {server.last_health_check 
                  ? format(new Date(server.last_health_check), 'dd/MM/yyyy HH:mm')
                  : 'Mai'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Endpoint</CardTitle>
        </CardHeader>
        <CardContent>
          <code className="text-sm bg-muted px-2 py-1 rounded">
            {server.endpoint}
          </code>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Autenticazione</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            <span>{server.auth_type === 'none' ? 'Nessuna' : server.auth_type}</span>
          </div>
        </CardContent>
      </Card>

      <Button 
        variant="outline" 
        className="w-full"
        onClick={() => healthCheckMutation.mutate()}
        disabled={healthCheckMutation.isPending}
      >
        {healthCheckMutation.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Activity className="mr-2 h-4 w-4" />
        )}
        Esegui Health Check
      </Button>
    </div>
  );
}

function inferCategory(toolName: string, description?: string | null): string {
  const parts = toolName.split('--');
  if (parts.length >= 2) {
    const prefix = parts[0];
    const action = parts[1];
    const actionMatch = action!.match(/^(Create|Delete|Update|Fetch|List|Read|Get|Set|Remove|Add|Check|Search|Download|Upload|Export|Import|Execute|Run|Send|Receive|Subscribe|Unsubscribe|Cancel|Verify|Validate|Generate|Deploy|Install|Uninstall|Assign|Revoke|Grant|Deny|Approve|Reject|Activate|Deactivate|Enable|Disable|Start|Stop|Pause|Resume|Reset|Clear|Flush|Purge|Archive|Restore|Clone|Copy|Move|Rename|Merge|Split|Connect|Disconnect|Attach|Detach|Bind|Unbind|Register|Unregister|Publish|Unpublish|Lock|Unlock|Open|Close|Join|Leave|Invite|Kick|Ban|Mute|Unmute|Block|Unblock)(.+)/)!;
    if (actionMatch) {
      const resource = actionMatch[2]!
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/^Sip /, 'SIP ')
        .replace(/^Api /, 'API ')
        .replace(/^Ip /, 'IP ')
        .replace(/\s+/g, ' ');
      return `${prefix} - ${resource}`;
    }
    return prefix || '';
  }
  
  if (description) {
    const descLower = description.toLowerCase();
    if (descLower.includes('call') || descLower.includes('voice')) return 'Chiamate';
    if (descLower.includes('message') || descLower.includes('sms')) return 'Messaggi';
    if (descLower.includes('phone') || descLower.includes('number')) return 'Numeri';
    if (descLower.includes('record')) return 'Registrazioni';
    if (descLower.includes('conference')) return 'Conferenze';
    if (descLower.includes('queue')) return 'Code';
    if (descLower.includes('sip')) return 'SIP';
    if (descLower.includes('account') || descLower.includes('user')) return 'Account';
  }
  
  return 'Altro';
}

function ToolsTab({ server }: { server: MCPServer }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [testingTool, setTestingTool] = useState<string | null>(null);
  const [testParams, setTestParams] = useState<string>('{}');
  const [testResult, setTestResult] = useState<unknown>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const { data: tools, isLoading } = useQuery({
    queryKey: ['mcp-tools', server.id],
    queryFn: async () => {
      const res = await api.get(`/mcp/servers/${server.id}/tools`);
      return res.data as MCPTool[];
    }
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/mcp/servers/${server.id}/sync`);
      return res.data;
    },
    onSuccess: () => {
      toast({ title: 'Tools sincronizzati' });
      queryClient.invalidateQueries({ queryKey: ['mcp-tools', server.id] });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
    }
  });

  const updatePermissionMutation = useMutation({
    mutationFn: async ({ toolName, updates }: { toolName: string; updates: Partial<MCPTool> }) => {
      const res = await api.patch(`/mcp/servers/${server.id}/tools/${encodeURIComponent(toolName)}`, updates);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-tools', server.id] });
      queryClient.invalidateQueries({ queryKey: ['mcp-all-tools'] });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
    }
  });

  const batchToggleMutation = useMutation({
    mutationFn: async ({ enabled, toolNames }: { enabled: boolean; toolNames?: string[] }) => {
      const res = await api.patch<{ updated: number }>(`/mcp/servers/${server.id}/tools/batch`, { 
        enabled, 
        ...(toolNames ? { tool_names: toolNames } : {})
      });
      return res;
    },
    onSuccess: (res) => {
      // Envelope permissivo: updated può stare sull'envelope o in data.
      const envelope = res as { updated?: number; data?: { updated?: number } };
      const count = envelope?.updated ?? envelope?.data?.updated ?? 0;
      toast({ title: `${count} tools aggiornati` });
      queryClient.invalidateQueries({ queryKey: ['mcp-tools', server.id] });
      queryClient.invalidateQueries({ queryKey: ['mcp-all-tools'] });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
    },
    onError: (error: unknown) => {
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Errore durante aggiornamento batch', variant: 'destructive' });
    }
  });

  const testToolMutation = useMutation({
    mutationFn: async ({ toolName, params }: { toolName: string; params: unknown }) => {
      const res = await api.post(`/mcp/servers/${server.id}/tools/${encodeURIComponent(toolName)}/test`, { params });
      return res.data;
    },
    onSuccess: (data) => {
      setTestResult(data);
      toast({ title: 'Test completato' });
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error);
      setTestResult({ error: message });
      toast({ title: 'Test fallito', description: message, variant: 'destructive' });
    }
  });

  const handleTestTool = (toolName: string) => {
    try {
      const params = JSON.parse(testParams);
      testToolMutation.mutate({ toolName, params });
    } catch {
      toast({ title: 'Errore', description: 'JSON parametri non valido', variant: 'destructive' });
    }
  };

  const toggleCategory = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const filteredTools = (tools || []).filter(t => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return t.tool_name.toLowerCase().includes(q) || 
           (t.description && t.description.toLowerCase().includes(q));
  });

  const categorizedTools: Record<string, MCPTool[]> = {};
  filteredTools.forEach(tool => {
    const cat = tool.category || inferCategory(tool.tool_name, tool.description);
    if (!categorizedTools[cat]) categorizedTools[cat] = [];
    categorizedTools[cat].push(tool);
  });
  const sortedCategories = Object.keys(categorizedTools).sort();

  const allEnabled = filteredTools.length > 0 && filteredTools.every(t => t.enabled !== false);
  const allDisabled = filteredTools.length > 0 && filteredTools.every(t => t.enabled === false);
  const enabledCount = filteredTools.filter(t => t.enabled !== false).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {enabledCount}/{tools?.length || 0} tools abilitati
          </span>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => batchToggleMutation.mutate({ enabled: true })}
                    disabled={batchToggleMutation.isPending || allEnabled}
                  >
                    <Power className="h-4 w-4 mr-1" />
                    Tutti ON
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Abilita tutti i tools</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => batchToggleMutation.mutate({ enabled: false })}
                    disabled={batchToggleMutation.isPending || allDisabled}
                  >
                    <PowerOff className="h-4 w-4 mr-1" />
                    Tutti OFF
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Disabilita tutti i tools</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button 
              size="sm" 
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Sync
            </Button>
          </div>
        </div>
        {(tools?.length || 0) > 10 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : sortedCategories.length > 0 ? (
        <div className="space-y-2">
          {sortedCategories.length > 1 && (
            <div className="flex gap-2 mb-2">
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => setCollapsedCategories(new Set())}>
                Espandi tutto
              </Button>
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => setCollapsedCategories(new Set(sortedCategories))}>
                Comprimi tutto
              </Button>
            </div>
          )}
          {sortedCategories.map(cat => {
            const catTools = categorizedTools[cat];
            const isCollapsed = collapsedCategories.has(cat);
            const catEnabledCount = catTools!.filter(t => t.enabled !== false).length!;
            const catAllEnabled = catTools!.every(t => t.enabled !== false)!;
            const catAllDisabled = catTools!.every(t => t.enabled === false)!;
            const catToolNames = catTools!.map(t => t.tool_name)!;

            return (
              <div key={cat} className="border rounded-lg overflow-hidden">
                <div 
                  className="flex items-center justify-between px-3 py-2 bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors"
                  onClick={() => toggleCategory(cat)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isCollapsed ? <ChevronRight className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
                    <FolderOpen className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <span className="font-medium text-sm truncate">{cat}</span>
                    <Badge variant="secondary" className="text-xs ml-1">
                      {catEnabledCount}/{catTools!.length!}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 p-0"
                            onClick={() => batchToggleMutation.mutate({ enabled: true, toolNames: catToolNames })}
                            disabled={batchToggleMutation.isPending || catAllEnabled}
                          >
                            <Power className="h-3.5 w-3.5 text-green-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Abilita tutti in "{cat}"</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 p-0"
                            onClick={() => batchToggleMutation.mutate({ enabled: false, toolNames: catToolNames })}
                            disabled={batchToggleMutation.isPending || catAllDisabled}
                          >
                            <PowerOff className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Disabilita tutti in "{cat}"</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                {!isCollapsed && (
                  <Table>
                    <TableBody>
                      {catTools!.map((tool) => (
                        <TableRow key={tool.id}>
                          <TableCell>
                            <div className="flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">{tool.tool_name}</div>
                                {tool.description && (
                                  <div className="text-xs text-muted-foreground line-clamp-1">
                                    {tool.description}
                                  </div>
                                )}
                              </div>
                              {tool.description && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary cursor-help flex-shrink-0 mt-0.5" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                      <p className="text-sm">{tool.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="w-16">
                            <Switch
                              checked={tool.enabled !== false}
                              onCheckedChange={(checked) => 
                                updatePermissionMutation.mutate({ 
                                  toolName: tool.tool_name, 
                                  updates: { enabled: checked } 
                                })
                              }
                            />
                          </TableCell>
                          <TableCell className="w-12">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setTestingTool(tool.tool_name)}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Wrench className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Nessun tool disponibile</p>
          <p className="text-xs">Clicca "Sync Tools" per recuperare i tools dal server</p>
        </div>
      )}

      <Dialog open={!!testingTool} onOpenChange={(o) => { if (!o) { setTestingTool(null); setTestResult(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Tool: {testingTool}</DialogTitle>
            <DialogDescription>Inserisci i parametri JSON per testare il tool</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label>Parametri (JSON)</Label>
              <Textarea
                value={testParams}
                onChange={(e) => setTestParams(e.target.value)}
                placeholder='{"param1": "value1"}'
                className="font-mono text-sm"
              />
            </div>
            {!!testResult && (
              <div className="grid gap-2">
                <Label>Risultato</Label>
                <pre className="bg-muted p-2 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              onClick={() => testingTool && handleTestTool(testingTool)}
              disabled={testToolMutation.isPending}
            >
              {testToolMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Esegui Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatsTab({ server }: { server: MCPServer }) {
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [agentFilter, setAgentFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['mcp-stats', server.id, refreshKey, dateFrom, dateTo, agentFilter],
    queryFn: async () => {
      const qp = new URLSearchParams({ date_from: dateFrom || '', date_to: dateTo || '' });
      if (agentFilter && agentFilter !== 'all') qp.set('agent_id', agentFilter);
      const res = await api.get<{
        summary: { total_calls: number; success_count: number; error_count: number; success_rate: number; avg_duration_ms: number };
        by_tool: { tool_name: string; calls: number; successes: number; errors: number; success_rate: number; avg_ms: number }[];
        by_agent: { agent_id: string; agent_name: string; calls: number; successes: number; success_rate: number }[];
        available_agents: { agent_id: string; agent_name: string }[];
      }>(`/mcp/servers/${server.id}/stats?${qp.toString()}`);
      return res.data;
    }
  });

  const successRateColor = (rate: number) =>
    rate >= 80 ? 'text-green-600' : rate >= 60 ? 'text-yellow-600' : 'text-red-600';

  const successRateBadge = (rate: number) =>
    rate >= 80
      ? 'bg-green-100 text-green-700 border-green-200'
      : rate >= 60
      ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
      : 'bg-red-100 text-red-700 border-red-200';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Dal</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="h-8 text-sm w-36"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Al</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="h-8 text-sm w-36"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Agente</Label>
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="h-8 text-sm w-48">
              <SelectValue placeholder="Tutti gli agenti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli agenti</SelectItem>
              {data?.available_agents?.map(a => (
                <SelectItem key={a.agent_id} value={a.agent_id}>{a.agent_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => setRefreshKey(k => k + 1)}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Aggiorna
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-12 text-destructive gap-2">
          <XCircle className="h-5 w-5" />
          <span className="text-sm">Errore nel caricamento delle statistiche</span>
        </div>
      ) : !data || data.summary.total_calls === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nessuna chiamata nel periodo selezionato</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border shadow-none">
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Chiamate Totali</span>
                </div>
                <div className="text-2xl font-bold">{data.summary.total_calls.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Tasso Successo</span>
                </div>
                <div className={`text-2xl font-bold ${successRateColor(data.summary.success_rate)}`}>
                  {data.summary.success_rate.toFixed(1)}%
                </div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Durata Media</span>
                </div>
                <div className="text-2xl font-bold">{data.summary.avg_duration_ms}<span className="text-sm font-normal text-muted-foreground ml-1">ms</span></div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Errori</span>
                </div>
                <div className={`text-2xl font-bold ${data.summary.error_count > 0 ? 'text-red-600' : ''}`}>
                  {data.summary.error_count.toLocaleString()}
                </div>
              </CardContent>
            </Card>
          </div>

          {data.by_tool.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2">Utilizzo per Tool</h4>
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead className="text-xs">Tool</TableHead>
                      <TableHead className="text-xs text-right">Chiamate</TableHead>
                      <TableHead className="text-xs text-right">Successi</TableHead>
                      <TableHead className="text-xs text-right">Errori</TableHead>
                      <TableHead className="text-xs text-right">% Successo</TableHead>
                      <TableHead className="text-xs text-right">Durata Media</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.by_tool.map(tool => (
                      <TableRow key={tool.tool_name}>
                        <TableCell className="py-2">
                          <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                            {tool.tool_name}
                          </code>
                        </TableCell>
                        <TableCell className="text-right text-sm py-2">{tool.calls}</TableCell>
                        <TableCell className="text-right text-sm py-2 text-green-600">{tool.successes}</TableCell>
                        <TableCell className="text-right text-sm py-2 text-red-600">{tool.errors}</TableCell>
                        <TableCell className="text-right py-2">
                          <Badge variant="outline" className={`text-xs ${successRateBadge(tool.success_rate)}`}>
                            {tool.success_rate.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm py-2 text-muted-foreground">
                          {tool.avg_ms != null ? `${tool.avg_ms}ms` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {data.by_agent.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                <Bot className="h-4 w-4" />
                Utilizzo per Agente
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {data.by_agent.map(agent => (
                  <div
                    key={agent.agent_id}
                    className="flex items-center justify-between rounded-md border px-3 py-2.5 bg-muted/20"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <span className="text-sm font-medium">{agent.agent_name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="secondary" className="text-xs">{agent.calls} chiamate</Badge>
                      <Badge variant="outline" className={`text-xs ${successRateBadge(agent.success_rate)}`}>
                        {agent.success_rate.toFixed(1)}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AuditTab({ server }: { server: MCPServer }) {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'error'>('all');

  const { data: logs, isLoading } = useQuery({
    queryKey: ['mcp-audit', server.id],
    queryFn: async () => {
      const res = await api.get(`/mcp/servers/${server.id}/audit`);
      return (res.data as MCPAuditLog[]) || [];
    }
  });

  const filteredLogs = logs?.filter(log => {
    if (statusFilter !== 'all' && log.status !== statusFilter) return false;
    if (filter && !log.tool_name.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Cerca tool..." 
            className="pl-8"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'success' | 'error')}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="success">Successo</SelectItem>
            <SelectItem value="error">Errore</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : filteredLogs && filteredLogs.length > 0 ? (
        <ScrollArea className="h-[300px]">
          <div className="space-y-2">
            {filteredLogs.map((log) => (
              <Card key={log.id}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {log.status === 'success' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <span className="font-medium">{log.tool_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{log.duration_ms}ms</span>
                      <span>{format(new Date(log.created_at), 'dd/MM HH:mm:ss')}</span>
                    </div>
                  </div>
                  {log.error_message && (
                    <div className="mt-1 text-xs text-red-500">{log.error_message}</div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Nessun log disponibile</p>
        </div>
      )}
    </div>
  );
}

function SettingsTab({ server }: { server: MCPServer }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [timeout, setTimeout] = useState(server.timeout_ms);
  const [retryCount, setRetryCount] = useState(server.retry_count);

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<MCPServer>) => {
      const res = await api.patch(`/mcp/servers/${server.id}`, updates);
      return res.data;
    },
    onSuccess: () => {
      toast({ title: 'Impostazioni salvate' });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
    }
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Rate Limits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label>Timeout (ms)</Label>
            <Input 
              type="number" 
              value={timeout}
              onChange={(e) => setTimeout(parseInt(e.target.value) || 30000)}
            />
          </div>
          <div className="grid gap-2">
            <Label>Retry Count</Label>
            <Input 
              type="number" 
              value={retryCount}
              min={0}
              max={10}
              onChange={(e) => setRetryCount(parseInt(e.target.value) || 3)}
            />
          </div>
          <Button 
            onClick={() => updateMutation.mutate({ timeout_ms: timeout, retry_count: retryCount })}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Salva Impostazioni
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Task #1829 — Catalogo MCP preset ufficiali + listener Socket.IO realtime.
import CatalogTab from './CatalogTab';
import { useMcpRealtimeInvalidation } from '@/hooks/useMcpRealtimeInvalidation';

export default function MCPToolsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  useMcpRealtimeInvalidation();
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<MCPServer | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [mainTab, setMainTab] = useState<'servers' | 'actions'>('servers');
  // Task #1829 — selettore Catalogo (preset ufficiali) vs Custom (wizard self-hosted).
  const [topTab, setTopTab] = useState<'catalog' | 'custom'>('catalog');

  const { data: servers, isLoading } = useQuery({
    queryKey: ['mcp-servers'],
    queryFn: async () => {
      const res = await api.get<MCPServer[] | { data?: MCPServer[] }>('/mcp/servers');
      // Handle both formats: direct array or { data: [...] }
      const responseData = res.data;
      if (Array.isArray(responseData)) {
        return responseData as MCPServer[];
      }
      return (responseData)?.data as MCPServer[];
    },
    refetchInterval: 30000
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/mcp/servers/${id}`);
      return res.data;
    },
    onSuccess: () => {
      toast({ title: 'Server eliminato' });
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
      setSelectedServer(null);
    }
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const res = await api.patch(`/mcp/servers/${id}`, { enabled });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
    }
  });

  const selectedServerData = servers?.find(s => s.id === selectedServer);

  useEffect(() => {
    if (servers && servers.length > 0 && !selectedServer) {
      setSelectedServer(servers[0]!.id!);
    }
  }, [servers, selectedServer]);

  return (
    <div className="space-y-6">
      <SectionNav
        sections={MCP_TOP_SECTIONS}
        value={{ section: topTab }}
        onChange={(v) => setTopTab(v.section as 'catalog' | 'custom')}
        ariaLabel="Sezioni MCP"
      />
      {topTab === 'catalog' && (
        <div className="pt-4">
          <CatalogTab />
        </div>
      )}
      {topTab === 'custom' && (
        <div className="pt-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                MCP Servers
              </CardTitle>
              <CardDescription>
                Gestisci i server MCP per estendere le capacità degli AI agent con CRM, Calendar, ERP
              </CardDescription>
            </div>
            <Button onClick={() => { setEditingServer(null); setWizardOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" /> Aggiungi Server
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : servers && servers.length > 0 ? (
            <div className="flex flex-col gap-6">
              {/* Server Table - Top Section */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[250px]">Nome Server</TableHead>
                      <TableHead>Transport</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Tools</TableHead>
                      <TableHead>Ultimo Check</TableHead>
                      <TableHead className="text-center">Abilitato</TableHead>
                      <TableHead className="w-[90px]">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {servers.map((server) => (
                      <TableRow
                        key={server.id}
                        className={`cursor-pointer transition-colors ${
                          selectedServer === server.id 
                            ? 'bg-primary/10 hover:bg-primary/15' 
                            : 'hover:bg-muted/50'
                        }`}
                        onClick={() => setSelectedServer(server.id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Server className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium">{server.name}</div>
                              {server.description && (
                                <div className="text-xs text-muted-foreground line-clamp-1">{server.description}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{server.transport}</Badge>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={server.health_status} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Wrench className="h-3 w-3 text-muted-foreground" />
                            <span>{server.tool_count}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {server.last_health_check 
                              ? format(new Date(server.last_health_check), 'dd/MM HH:mm')
                              : '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={server.enabled}
                            onClick={(e) => e.stopPropagation()}
                            onCheckedChange={(checked) => 
                              toggleMutation.mutate({ id: server.id, enabled: checked })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {server.transport !== 'internal' && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingServer(server);
                                  setWizardOpen(true);
                                }}
                                title="Modifica server"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('Eliminare questo server?')) {
                                  deleteMutation.mutate(server.id);
                                }
                              }}
                              title="Elimina server"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Server Details Workspace - Bottom Section */}
              {selectedServerData ? (
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Server className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{selectedServerData.name}</CardTitle>
                          {selectedServerData.description && (
                            <CardDescription>{selectedServerData.description}</CardDescription>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={selectedServerData.health_status} />
                        <Badge variant="outline">{selectedServerData.transport}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                      <TabsList className="grid grid-cols-5 w-full max-w-xl">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="tools">Tools</TabsTrigger>
                        <TabsTrigger value="stats">Statistiche</TabsTrigger>
                        <TabsTrigger value="audit">Audit</TabsTrigger>
                        <TabsTrigger value="settings">Settings</TabsTrigger>
                      </TabsList>
                      <TabsContent value="overview" className="mt-4">
                        <OverviewTab server={selectedServerData} />
                      </TabsContent>
                      <TabsContent value="tools" className="mt-4">
                        <ToolsTab server={selectedServerData} />
                      </TabsContent>
                      <TabsContent value="stats" className="mt-4">
                        <StatsTab server={selectedServerData} />
                      </TabsContent>
                      <TabsContent value="audit" className="mt-4">
                        <AuditTab server={selectedServerData} />
                      </TabsContent>
                      <TabsContent value="settings" className="mt-4">
                        <SettingsTab server={selectedServerData} />
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
                    <div className="text-center">
                      <Server className="h-10 w-10 mx-auto mb-3 opacity-40" />
                      <p className="font-medium">Seleziona un server dalla tabella</p>
                      <p className="text-sm">per visualizzare e gestire i dettagli</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Nessun server MCP configurato</p>
              <p className="text-sm mb-4">Aggiungi un server MCP per iniziare</p>
              <Button onClick={() => { setEditingServer(null); setWizardOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" /> Aggiungi Server
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
        </div>
      )}

      <AddServerWizard 
        open={wizardOpen} 
        onOpenChange={(open) => {
          setWizardOpen(open);
          if (!open) setEditingServer(null);
        }}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['mcp-servers'] })}
        editServer={editingServer}
      />
    </div>
  );
}
